import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Star, CheckCircle2, XCircle, Moon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Screen = ({ children }) => (
  <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-4" dir="rtl">{children}</div>
);

export default function RatingPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/rate/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "الرابط غير صالح"));
  }, [token]);

  const submit = async () => {
    if (stars < 1) return;
    setBusy(true);
    try {
      await axios.post(`${API}/public/rate/${token}`, { stars, comment });
      setDone(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "حدث خطأ");
    } finally { setBusy(false); }
  };

  if (error) return <Screen><Card className="p-8 max-w-md text-center border-red-200">
    <XCircle className="h-14 w-14 text-red-500 mx-auto mb-4" />
    <p className="text-slate-700 text-lg">{error}</p>
  </Card></Screen>;

  if (done) return <Screen><Card className="p-10 max-w-md text-center border-emerald-200">
    <CheckCircle2 className="h-16 w-16 text-emerald-600 mx-auto mb-4" />
    <h2 data-testid="rate-done" className="font-heading text-3xl font-black text-emerald-900 mb-3">شكراً لك 🌟</h2>
    <p className="text-slate-700">تم استلام تقييمك، نتمنى لك عودة حميدة.</p>
  </Card></Screen>;

  if (!info) return <Screen><div className="text-emerald-900">جارٍ التحميل...</div></Screen>;

  return (
    <div className="min-h-screen bg-[#FDFBF7]" dir="rtl">
      <header className="bg-emerald-950 text-white">
        <div className="max-w-2xl mx-auto px-4 py-6 flex items-center gap-4">
          {info.logo_base64 ? (
            <img src={info.logo_base64} alt="" className="h-14 w-14 rounded-xl object-cover" />
          ) : (
            <div className="h-14 w-14 rounded-xl bg-amber-500 flex items-center justify-center">
              <Moon className="h-8 w-8 text-emerald-950" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold truncate">{info.association_name}</div>
            {info.campaign_name && <div className="text-amber-300 text-sm truncate">حملة: {info.campaign_name}</div>}
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-8">
        <Card className="p-8 border-emerald-900/10">
          <h1 className="font-heading text-2xl sm:text-3xl font-black text-emerald-950 mb-2">
            كيف كانت رحلتك مع {info.association_name}؟
          </h1>
          {info.beneficiary_name && (
            <p className="text-slate-600 mb-6">أهلاً {info.beneficiary_name}، شاركنا انطباعك.</p>
          )}

          <div className="mb-6">
            <p className="text-sm text-slate-700 mb-3 font-bold">قيّم تجربتك:</p>
            <div className="flex justify-center gap-2" dir="ltr">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  data-testid={`rate-star-${n}`}
                  onClick={() => setStars(n)}
                  className="p-1 hover:scale-110 transition-transform"
                  aria-label={`${n} نجوم`}
                >
                  <Star
                    className={`h-10 w-10 sm:h-12 sm:w-12 ${n <= stars ? "fill-amber-400 text-amber-400" : "text-slate-300"}`}
                    strokeWidth={1.5}
                  />
                </button>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <label className="text-sm text-slate-700 mb-2 block font-bold">ملاحظات (اختياري)</label>
            <Textarea
              data-testid="rate-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="أخبرنا المزيد عن تجربتك..."
              rows={4}
            />
          </div>

          <Button
            data-testid="rate-submit"
            disabled={stars < 1 || busy}
            onClick={submit}
            className="w-full h-12 bg-emerald-900 hover:bg-emerald-800 text-white font-bold text-base"
          >
            {busy ? "جارٍ الإرسال..." : "إرسال التقييم"}
          </Button>
        </Card>
      </div>
    </div>
  );
}
